(function () {
  const MAPS_SCRIPT_ID = 'agrotropic-google-maps-script';
  let mapsLoadPromise = null;

  function trimValue(value) {
    return typeof value === 'string' ? value.trim() : '';
  }

  function loadGoogleMaps(apiKey) {
    const normalizedKey = trimValue(apiKey);

    if (!normalizedKey) {
      return Promise.reject(new Error('Missing Google Maps API key'));
    }

    if (window.google && window.google.maps) {
      return Promise.resolve();
    }

    if (mapsLoadPromise) {
      return mapsLoadPromise;
    }

    mapsLoadPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(MAPS_SCRIPT_ID);

      if (existingScript) {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }

        existingScript.addEventListener('load', () => {
          if (window.google && window.google.maps) {
            resolve();
          } else {
            reject(new Error('Google Maps loaded without maps library'));
          }
        });
        existingScript.addEventListener('error', () => reject(new Error('Google Maps failed to load')));
        return;
      }

      window.agrotropicGoogleMapsCallbacks = window.agrotropicGoogleMapsCallbacks || [];
      window.agrotropicGoogleMapsCallbacks.push(resolve);

      window.agrotropicGoogleMapsInit = function () {
        const callbacks = window.agrotropicGoogleMapsCallbacks || [];
        window.agrotropicGoogleMapsCallbacks = [];
        callbacks.forEach((callback) => callback());
      };

      const script = document.createElement('script');
      script.id = MAPS_SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(normalizedKey)}&loading=async&callback=agrotropicGoogleMapsInit`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        mapsLoadPromise = null;
        reject(new Error('Google Maps failed to load'));
      };
      document.head.appendChild(script);
    }).catch((error) => {
      mapsLoadPromise = null;
      throw error;
    });

    return mapsLoadPromise;
  }

  function parseNumber(value) {
    const number = parseFloat(trimValue(value));
    return Number.isFinite(number) ? number : null;
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getLocationTitle(locationTitle, address) {
    const customTitle = trimValue(locationTitle);
    if (customTitle) {
      return customTitle;
    }

    const firstLine = trimValue(address).split('\n')[0];
    if (!firstLine) {
      return '';
    }

    return firstLine.split(',')[0].trim();
  }

  function buildDirectionsUrl(destination, origin) {
    const params = new URLSearchParams({ api: '1', destination: destination });
    if (origin) {
      params.set('origin', origin);
    }
    return `https://www.google.com/maps/dir/?${params.toString()}`;
  }

  function buildGoogleMapsPlaceUrl(destination, lat, lng) {
    if (lat !== null && lng !== null) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
    }

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(destination)}`;
  }

  class AgrotropicTextMap extends HTMLElement {
    constructor() {
      super();
      this.handleRouteClick = this.handleRouteClick.bind(this);
      this.handlePlaceCardOpenMaps = this.handlePlaceCardOpenMaps.bind(this);
      this.handlePlaceCardDirections = this.handlePlaceCardDirections.bind(this);
    }

    connectedCallback() {
      this.mapElement = this.querySelector('[data-agrotropic-text-map-canvas]');
      this.placeCard = this.querySelector('[data-agrotropic-text-map-card]');
      this.routeButton = this.querySelector('[data-agrotropic-text-map-route]');
      this.apiKey = trimValue(this.dataset.apiKey);
      this.address = trimValue(this.dataset.address);
      this.locationTitle = trimValue(this.dataset.locationTitle);
      this.lat = parseNumber(this.dataset.lat);
      this.lng = parseNumber(this.dataset.lng);
      this.zoom = parseInt(this.dataset.zoom, 10) || 15;
      this.useCurrentLocation = this.dataset.useCurrentLocation === 'true';

      this.map = null;
      this.marker = null;
      this.directionsRenderer = null;
      this.destination = null;
      this.displayAddress = '';
      this.isInitialized = false;

      if (this.routeButton) {
        this.routeButton.removeEventListener('click', this.handleRouteClick);
        this.routeButton.addEventListener('click', this.handleRouteClick);
      }

      this.initMap();
    }

    disconnectedCallback() {
      if (this.routeButton) {
        this.routeButton.removeEventListener('click', this.handleRouteClick);
      }
    }

    showMapError(message) {
      if (!this.mapElement) return;

      this.hidePlaceCard();
      this.mapElement.hidden = false;
      this.mapElement.innerHTML = `<p class="agrotropic-text-map__map-error">${escapeHtml(message)}</p>`;
    }

    hidePlaceCard() {
      if (!this.placeCard) return;
      this.placeCard.hidden = true;
      this.placeCard.innerHTML = '';
    }

    renderPlaceCard(title, address) {
      if (!this.placeCard) return;

      const safeTitle = escapeHtml(title);
      const safeAddress = escapeHtml(address);
      const titleMarkup =
        safeTitle && safeTitle !== safeAddress
          ? `<p class="agrotropic-text-map__place-card-title">${safeTitle}</p>`
          : '';
      const addressMarkup =
        safeTitle && safeTitle !== safeAddress
          ? `<p class="agrotropic-text-map__place-card-address">${safeAddress}</p>`
          : `<p class="agrotropic-text-map__place-card-title">${safeAddress}</p>`;

      this.placeCard.innerHTML = `
        <div class="agrotropic-text-map__place-card-text">
          ${titleMarkup}
          ${addressMarkup}
        </div>
        <div class="agrotropic-text-map__place-card-actions">
          <button
            type="button"
            class="agrotropic-text-map__place-card-action"
            data-agrotropic-text-map-open-maps
            aria-label="In Google Maps öffnen"
          >
            <i class="fa-solid fa-up-right-from-square" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            class="agrotropic-text-map__place-card-action"
            data-agrotropic-text-map-directions
            aria-label="Route planen"
          >
            <i class="fa-solid fa-diamond-turn-right" aria-hidden="true"></i>
          </button>
        </div>
      `;

      this.placeCard.hidden = false;

      const openMapsButton = this.placeCard.querySelector('[data-agrotropic-text-map-open-maps]');
      const directionsButton = this.placeCard.querySelector('[data-agrotropic-text-map-directions]');

      if (openMapsButton) {
        openMapsButton.addEventListener('click', this.handlePlaceCardOpenMaps);
      }

      if (directionsButton) {
        directionsButton.addEventListener('click', this.handlePlaceCardDirections);
      }
    }

    initMap() {
      if (!this.mapElement) {
        return;
      }

      if (!this.apiKey || !this.address) {
        return;
      }

      if (this.lat !== null && this.lng === null) {
        this.showMapError('Please provide both latitude and longitude, or leave both empty.');
        return;
      }

      if (this.lng !== null && this.lat === null) {
        this.showMapError('Please provide both latitude and longitude, or leave both empty.');
        return;
      }

      this.mapElement.innerHTML = '';
      this.mapElement.hidden = false;
      this.hidePlaceCard();

      loadGoogleMaps(this.apiKey)
        .then(() => this.renderMap())
        .catch(() => {
          this.showMapError(
            'Google Maps could not be loaded. Enable the Maps JavaScript API for this key and check domain restrictions.'
          );
        });
    }

    resolveDestination() {
      if (this.lat !== null && this.lng !== null) {
        return Promise.resolve({
          lat: this.lat,
          lng: this.lng,
          formattedAddress: this.address,
        });
      }

      return new Promise((resolve, reject) => {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode(
          {
            address: this.address,
            region: 'CH',
          },
          (results, status) => {
            if (status === 'OK' && results && results[0]) {
              const location = results[0].geometry.location;
              resolve({
                lat: location.lat(),
                lng: location.lng(),
                formattedAddress: results[0].formatted_address || this.address,
              });
              return;
            }

            reject(new Error(`Geocoding failed: ${status}`));
          }
        );
      });
    }

    renderMap() {
      return this.resolveDestination()
        .then((destination) => {
          this.destination = {
            lat: destination.lat,
            lng: destination.lng,
          };

          const locationTitle = getLocationTitle(this.locationTitle, this.address);
          this.displayAddress = destination.formattedAddress || this.address;

          this.map = new google.maps.Map(this.mapElement, {
            center: this.destination,
            zoom: this.zoom,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          });

          this.marker = new google.maps.Marker({
            position: this.destination,
            map: this.map,
            title: locationTitle || this.displayAddress,
          });

          this.renderPlaceCard(locationTitle, this.displayAddress);

          google.maps.event.trigger(this.map, 'resize');
          this.map.setCenter(this.destination);
          this.isInitialized = true;
        })
        .catch((error) => {
          const status = error && error.message ? error.message.replace('Geocoding failed: ', '') : '';
          let message = 'The address could not be located on the map. Check the address or add latitude/longitude.';

          if (status === 'REQUEST_DENIED') {
            message =
              'Geocoding was denied for this API key. Enable the Geocoding API and check key restrictions.';
          } else if (status === 'ZERO_RESULTS') {
            message = 'No location found for this address. Try a more complete address or add coordinates.';
          }

          this.showMapError(message);
        });
    }

    openGoogleMapsPlace() {
      const url = buildGoogleMapsPlaceUrl(
        this.displayAddress || this.address,
        this.destination ? this.destination.lat : null,
        this.destination ? this.destination.lng : null
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    handlePlaceCardOpenMaps() {
      this.openGoogleMapsPlace();
    }

    handlePlaceCardDirections() {
      this.handleRouteClick();
    }

    openExternalDirections(origin) {
      const destination = this.address;
      if (!destination) return;

      const url = buildDirectionsUrl(
        destination,
        origin ? `${origin.lat},${origin.lng}` : null
      );
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    showDirectionsOnMap(origin) {
      if (!this.map || !this.destination) {
        this.openExternalDirections(origin);
        return;
      }

      const directionsService = new google.maps.DirectionsService();

      if (!this.directionsRenderer) {
        this.directionsRenderer = new google.maps.DirectionsRenderer({
          suppressMarkers: false,
        });
        this.directionsRenderer.setMap(this.map);
      }

      directionsService.route(
        {
          origin: origin,
          destination: this.destination,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === 'OK') {
            this.directionsRenderer.setDirections(result);
            return;
          }

          this.openExternalDirections(origin);
        }
      );
    }

    handleRouteClick() {
      if (!this.address) {
        return;
      }

      if (!this.useCurrentLocation) {
        this.openExternalDirections();
        return;
      }

      if (!navigator.geolocation) {
        this.openExternalDirections();
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const origin = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };

          if (window.google && this.map && this.destination) {
            this.showDirectionsOnMap(origin);
            return;
          }

          this.openExternalDirections(origin);
        },
        () => {
          this.openExternalDirections();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    }
  }

  if (!customElements.get('agrotropic-text-map')) {
    customElements.define('agrotropic-text-map', AgrotropicTextMap);
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('agrotropic-text-map').forEach((element) => {
      if (typeof element.initMap === 'function') {
        element.initMap();
      }
    });
  });
})();
